import React from 'react';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import {
  Avatar,
  ActionButton,
  Badges,
  InlineTextButton,
  NamedLink,
  Icon,
} from '../../../../components';
import { createSlug } from '../../../../util/urlHelpers';
import classNames from 'classnames';
import moment from 'moment';

import { usePostVotes, usePostContent, usePostMetadata } from './Post.shared';
import ImageSlider from './ImageSlider/ImageSlider';
import css from './Post.module.css';

const Post = props => {
  const intl = useIntl();
  const {
    rootClassName,
    className,
    post,
    currentUser,
    linkPreview,
    categories,
    location,
    history,
    onAddPostCount,
  } = props;

  // Use custom hooks to separate logic from presentation
  const { voteCount, userVote, handleVote } = usePostVotes(
    post,
    currentUser,
    onAddPostCount,
    location,
    history
  );

  const { isTextPost, isImagePost, isLinkPost, images, linkUrl, description } = usePostContent(
    post
  );

  const { author, authorDisplayName, createdAt, commentsLength, categoryLabel } = usePostMetadata(
    post,
    categories
  );

  const { title } = post.attributes;
  const postedAt = moment(createdAt).fromNow();
  const classes = classNames(rootClassName || css.root, className);

  // Create author link component
  const authorLink = (
    <span>
      <FormattedMessage id="CommunityPage.userDisplayName" values={{ authorDisplayName }} />
    </span>
  );

  return (
    <NamedLink
      className={classes}
      name="PostPage"
      params={{ id: post.id.uuid, slug: createSlug(title) }}
      to={{ state: { search: location.search } }}
    >
      {/* Post Header with Avatar */}
      <div className={css.wrapper}>
        <Avatar
          className={css.avatar}
          initialsClassName={css.avatarInitials}
          user={author}
          useUserTier
          disableProfileLink
        />
        <div className={css.content}>
          <div className={css.info}>
            <FormattedMessage
              id="CommunityPage.postedBy"
              values={{
                category: categoryLabel,
                badges: (
                  <Badges
                    className={css.badges}
                    iconSize={16}
                    imageClassName={css.badgeImage}
                    user={author}
                  />
                ),
                userDisplayName: authorLink,
                postedAt,
              }}
            />
          </div>
        </div>
      </div>

      {/* Post Title */}
      <h3 className={css.title}>{title}</h3>

      {/* Link Post URL (when no preview available) */}
      {renderLinkUrl(isLinkPost, linkUrl, linkPreview)}

      {/* Text Post Content */}
      {renderTextPost(isTextPost, description)}

      {/* Image Post Content */}
      {renderImagePost(isImagePost, images, title)}

      {/* Link Preview */}
      {renderLinkPreview(isLinkPost, linkPreview, linkUrl, title)}

      {/* Post Actions (Votes & Comments) */}
      <div className={css.postActions}>
        {renderVoteActions(voteCount, userVote, handleVote, intl)}
        <ActionButton className={css.commentActionButton} label={commentsLength} isButton={false}>
          <Icon name="comment" size={16} className={css.actionIcon} />
        </ActionButton>
      </div>
    </NamedLink>
  );
};

// Helper function to render link URL
const renderLinkUrl = (isLinkPost, linkUrl, linkPreview) => {
  if (!isLinkPost || !linkUrl || linkPreview) {
    return null;
  }

  return (
    <div className={css.linkUrl}>
      <InlineTextButton
        className={css.linkAnchor}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          window.open(linkUrl, '_blank');
        }}
      >
        {linkUrl}
      </InlineTextButton>
    </div>
  );
};

// Helper function to render text post content
const renderTextPost = (isTextPost, description) => {
  if (!isTextPost || !description) return null;

  return (
    <div className={css.textPost}>
      <p className={css.textPostContent}>{description}</p>
    </div>
  );
};

// Helper function to render image post content
const renderImagePost = (isImagePost, images, title) => {
  if (!isImagePost || !images?.length) return null;

  return (
    <div className={css.imageSlider}>
      <ImageSlider images={images} title={title} />
    </div>
  );
};

// Helper function to render link preview
const renderLinkPreview = (isLinkPost, linkPreview, linkUrl, title) => {
  if (!isLinkPost || !linkPreview || linkPreview.error) return null;

  return (
    <div className={css.linkPreview}>
      <InlineTextButton
        className={css.linkPreviewAnchor}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          window.open(linkUrl, '_blank');
        }}
      >
        {linkPreview.images?.[0] && (
          <img
            src={linkPreview.images[0]}
            alt={linkPreview.title || 'Link preview'}
            className={css.linkPreviewImage}
          />
        )}
        <div className={css.linkPreviewContent}>
          <span className={css.linkPreviewTitle}>{linkPreview.title || title}</span>
          <span className={css.linkPreviewDescription}>{linkPreview.description || linkUrl}</span>
          <span className={css.linkPreviewUrl}>{linkUrl}</span>
        </div>
      </InlineTextButton>
    </div>
  );
};

// Helper function to render vote actions
const renderVoteActions = (voteCount, userVote, handleVote, intl) => {
  return (
    <ActionButton className={css.voteActionButtonWrapper} isButton={false}>
      <InlineTextButton
        className={classNames(css.voteActionButton, {
          [css.voteActionButtonActive]: userVote === 'like',
        })}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          handleVote('like');
        }}
        aria-label={intl.formatMessage({ id: 'Post.upvote' })}
      >
        <Icon name="voteUp" size={16} className={css.voteActionIcon} filled={userVote === 'like'} />
      </InlineTextButton>
      <span className={css.voteCount}>{voteCount}</span>
      <InlineTextButton
        className={classNames(css.voteActionButton, css.dislikeVoteActionButton, {
          [css.voteActionButtonActive]: userVote === 'dislike',
        })}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          handleVote('dislike');
        }}
        aria-label={intl.formatMessage({ id: 'Post.downvote' })}
      >
        <Icon
          name="voteDown"
          size={16}
          className={css.voteActionIcon}
          filled={userVote === 'dislike'}
        />
      </InlineTextButton>
    </ActionButton>
  );
};

export default Post;
